/**
 * VersionsTab — surfaces the debate version tree, AI comparison report, and
 * persona prompt diff inside the Verdict panel as a single unified view.
 *
 * Composition contract:
 *   ┌───────────────────────────────────────────────────────────────────┐
 *   │  [Onboarding banner] (empty or single-version state only)          │
 *   │  [Action toolbar]   — Yeni Versiyon / Bağımsız Kopya               │
 *   │  [VersionTreePanel]                                                │
 *   │     • Renders parent → fork tree                                   │
 *   │     • Two checkboxes select [A, B] for comparison                  │
 *   │     • "Kıyasla" button → POST /api/debate/compare                  │
 *   │                                                                   │
 *   │  [PersonaDiffView] (only when A & B selected)                     │
 *   │     • Side-by-side system_instr diff per matched seat              │
 *   │                                                                   │
 *   │  [ComparisonReportView] (after `compare` resolves)                 │
 *   │     • Summary, key differences, charts, persona impact, tavsiye    │
 *   └───────────────────────────────────────────────────────────────────┘
 *
 * All centralised loading / error states live here so child components
 * remain pure presentational widgets.
 */
import { Suspense, lazy, useState } from "react";
import {
  Loader2,
  FileText,
  GitBranch,
  Sparkles,
  ArrowRight,
  Lightbulb,
  Copy,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useDebateStore } from "../../store/debateStore";
import VersionTreePanel from "../versions/VersionTreePanel";
import ComparisonReportView from "../versions/ComparisonReportView";
import PersonaDiffView from "../versions/PersonaDiffView";

const VersionActions = lazy(() => import("../versions/VersionActions"));

interface Props {
  debateId: string;
}

function OnboardingBanner({ debateId }: { debateId: string }) {
  // Single unified card — header + 3-step explainer + CTA buttons in
  // one cohesive surface. Previously the explainer and the action
  // banner were stacked as TWO separate cards which felt repetitive
  // ("Versiyon Yönetimi" then "Bu kararı farklı bir versiyonla test
  // edin" right beneath, both saying the same thing). The merged
  // layout puts the buttons directly under the steps so the visual
  // path is `intro → workflow → action` in one breath.
  const forkDebate = useDebateStore((s) => s.forkDebate);
  const duplicateDebate = useDebateStore((s) => s.duplicateDebate);
  const prefillFromDebate = useDebateStore((s) => s.prefillFromDebate);
  const [forking, setForking] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const busy = forking || duplicating;

  const handleFork = async () => {
    setForking(true);
    try {
      const newId = await forkDebate(debateId);
      if (newId) await prefillFromDebate(newId, debateId);
    } finally {
      setForking(false);
    }
  };
  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const newId = await duplicateDebate(debateId);
      if (newId) await prefillFromDebate(newId);
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div className="rounded-xl border border-violet-500/25 bg-gradient-to-br from-violet-500/[0.07] via-fuchsia-500/[0.04] to-indigo-500/[0.05] p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-300">
          <GitBranch className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-fg-strong">
            Versiyon Yönetimi & AI Kıyaslama
          </h3>
          <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">
            Aynı soruyu farklı kadrolar, parametreler veya prompt'larla
            yeniden müzakere ettir. <strong className="text-violet-300">Yeni
            Versiyon</strong> ebeveyne bağlı kalır ve karşılaştırılabilir;{" "}
            <strong className="text-cyan-300">Bağımsız Kopya</strong> sıfırdan
            yeni bir lineage başlatır.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
        <Step
          n={1}
          icon={GitBranch}
          title="Yeni Versiyon Türet"
          desc="Personaları kopyalar, parametreleri değiştirebilirsin. Üst tartışmaya bağlı kalır."
          accent="violet"
        />
        <Step
          n={2}
          icon={Sparkles}
          title="Tartışmayı Yürüt"
          desc="Yeni versiyon başlar; sonuçlar ayrı bir verdict olarak persiste edilir."
          accent="cyan"
        />
        <Step
          n={3}
          icon={FileText}
          title="AI ile Kıyasla"
          desc="İki versiyonu seç → 'Kıyasla' → Quantitative + qualitative rapor + grafikler."
          accent="amber"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          type="button"
          onClick={handleFork}
          disabled={busy}
          className="flex items-center justify-center gap-2 rounded-lg border border-violet-500/40 bg-violet-500/15 px-3 py-2.5 text-sm font-semibold text-violet-200 transition hover:bg-violet-500/25 hover:border-violet-400/60 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {forking ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <GitBranch className="h-4 w-4" aria-hidden />
          )}
          Yeni Versiyon (Fork)
        </button>
        <button
          type="button"
          onClick={handleDuplicate}
          disabled={busy}
          className="flex items-center justify-center gap-2 rounded-lg border border-cyan-500/40 bg-cyan-500/15 px-3 py-2.5 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-500/25 hover:border-cyan-400/60 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {duplicating ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Copy className="h-4 w-4" aria-hidden />
          )}
          Bağımsız Kopya
        </button>
      </div>
    </div>
  );
}

function Step({
  n,
  icon: Icon,
  title,
  desc,
  accent,
}: {
  n: number;
  icon: LucideIcon;
  title: string;
  desc: string;
  accent: "violet" | "cyan" | "amber";
}) {
  const accentMap = {
    violet: { ring: "ring-violet-500/30", text: "text-violet-300", bg: "bg-violet-500/10" },
    cyan: { ring: "ring-cyan-500/30", text: "text-cyan-300", bg: "bg-cyan-500/10" },
    amber: { ring: "ring-amber-500/30", text: "text-amber-300", bg: "bg-amber-500/10" },
  } as const;
  const a = accentMap[accent];
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${a.bg} ${a.text} ring-1 ${a.ring}`}
        >
          {n}
        </span>
        <Icon className={`h-3.5 w-3.5 ${a.text}`} />
        <span className="text-[12px] font-semibold text-fg-strong">{title}</span>
      </div>
      <p className="text-[11px] text-fg-muted leading-snug">{desc}</p>
    </div>
  );
}

export default function VersionsTab({ debateId }: Props) {
  const selected = useDebateStore((s) => s.selectedForCompare);
  const report = useDebateStore((s) => s.comparisonReport);
  const compareLoading = useDebateStore((s) => s.compareLoading);
  const compareError = useDebateStore((s) => s.compareError);
  const versions = useDebateStore((s) => s.versions);
  const versionsLoading = useDebateStore((s) => s.versionsLoading);

  const [a, b] = selected;
  const bothSelected = Boolean(a && b);

  // Resolve human-friendly labels for the diff header from the version
  // tree we already loaded — avoids a redundant SELECT just to display
  // "v1" / "v2" badges next to the persona panes.
  const labelFor = (id: string | null) => {
    if (!id) return "?";
    const v = versions.find((row) => row.id === id);
    return v?.version_label || `v${v?.version_index ?? "?"}`;
  };

  // Surface the onboarding banner whenever the lineage is still
  // standalone (single version) — gives the operator a clear path to
  // their first fork. Once a sibling exists, the banner collapses
  // into a compact action-bar at the top so the tree stays focal.
  const isStandalone = versions.length <= 1;

  return (
    <div className="flex flex-col gap-6">
      {versionsLoading && versions.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Versiyon ağacı yükleniyor…
        </div>
      ) : isStandalone ? (
        <OnboardingBanner debateId={debateId} />
      ) : (
        <>
          {/* Compact action-bar — kept above the tree so even returning
              users can spawn another sibling without scrolling. */}
          <div className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
            <div className="flex items-center gap-2 text-[12px] text-fg-muted">
              <Lightbulb className="h-3.5 w-3.5 text-amber-300/80" />
              <span>
                <strong className="text-fg-strong">{versions.length}</strong>{" "}
                versiyon var · 2'sini seçip "Kıyasla" diyebilirsin
              </span>
            </div>
            <Suspense fallback={null}>
              <VersionActions debateId={debateId} variant="compact" />
            </Suspense>
          </div>
          <VersionTreePanel debateId={debateId} />
        </>
      )}

      {bothSelected && (
        <div className="rounded-lg border border-line-soft/40 bg-surface-1/40 p-4">
          {compareLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-fg-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              AI kıyaslama raporu üretiliyor (Gemma 26B)…
            </div>
          )}

          {compareError && !compareLoading && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
              Kıyaslama başarısız: {compareError}
            </div>
          )}

          {!compareLoading && !compareError && !report && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-fg-muted">
              <ArrowRight className="h-4 w-4" />
              Yukarıdaki "Kıyasla" düğmesine basarak AI raporu üretebilirsiniz.
            </div>
          )}
        </div>
      )}

      {bothSelected && a && b && (
        <PersonaDiffView
          debateAId={a}
          debateBId={b}
          labelA={labelFor(a)}
          labelB={labelFor(b)}
        />
      )}

      {report && bothSelected && <ComparisonReportView />}
    </div>
  );
}
